import React, { useEffect, useState } from 'react';
import { Button, Grid, IconButton, LinearProgress } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  listTenantDraftsApi,
  createTenantDraftApi,
  deleteTenantDraftApi,
} from 'services/TenantService';

// The Create Tenant landing view. Two sections, per the requirement:
//   • "Continue creating" — the super admin's resumable drafts (multiple),
//     each shows completeness and resume/discard.
//   • "Provision a new tenant" — start a fresh draft.
// Selecting/creating a draft calls onOpenDraft(draftId) so the parent swaps in
// the tabbed form.
function CreateTenantLanding({ onOpenDraft }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    listTenantDraftsApi()
      .then((res) => setDrafts(res?.data || []))
      .catch(() => toast.error('Could not load your drafts.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleNew = async () => {
    setCreating(true);
    try {
      const res = await createTenantDraftApi();
      const id = res?.data?.id;
      if (id) onOpenDraft(id);
    } catch (e) {
      toast.error('Could not start a new draft.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, event) => {
    event.stopPropagation();
    // eslint-disable-next-line no-alert
    if (!window.confirm('Discard this draft? This cannot be undone.')) return;
    try {
      await deleteTenantDraftApi(id);
      setDrafts((prev) => prev.filter((d) => d.id !== id));
      toast.success('Draft discarded');
    } catch (e) {
      toast.error('Could not discard the draft.');
    }
  };

  return (
    <div className="p-5">
      {/* Provision a new tenant */}
      <div className="p-5 bg-white borderStyle" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <h2 className="text-lg font-semibold">Create a tenant</h2>
            <p style={{ fontSize: 13, color: '#666', margin: '4px 0 0' }}>
              Start a new tenant. You can save and continue any time — nothing is provisioned until
              you submit.
            </p>
          </div>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNew}
            disabled={creating}
          >
            {creating ? 'Starting…' : 'Provision a new tenant'}
          </Button>
        </div>
      </div>

      {/* Continue creating */}
      <div className="p-5 bg-white borderStyle">
        <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 4px' }}>Continue creating</h3>
        <p style={{ fontSize: 13, color: '#666', margin: '0 0 16px' }}>
          Drafts you started earlier. Pick up exactly where you left off.
        </p>

        {loading ? (
          <p style={{ fontSize: 13, color: '#888' }}>Loading drafts…</p>
        ) : drafts.length === 0 ? (
          <p style={{ fontSize: 13, color: '#888' }}>
            No drafts in progress. Provision a new tenant to begin.
          </p>
        ) : (
          <Grid container spacing={2}>
            {drafts.map((draft) => (
              <Grid item xs={12} sm={6} md={4} key={draft.id}>
                <div
                  onClick={() => onOpenDraft(draft.id)}
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    padding: 16,
                    cursor: 'pointer',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#0f538d' }}>
                      {draft.title || 'Untitled draft'}
                    </span>
                    <IconButton
                      size="small"
                      aria-label="discard draft"
                      onClick={(e) => handleDelete(draft.id, e)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 11,
                        color: '#888',
                        marginBottom: 4,
                      }}
                    >
                      <span>Completeness</span>
                      <span>{draft.completeness ?? 0}%</span>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={draft.completeness ?? 0}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </div>
                  {draft.updatedAt && (
                    <span style={{ fontSize: 11, color: '#aaa' }}>
                      Last edited {new Date(draft.updatedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </Grid>
            ))}
          </Grid>
        )}
      </div>
    </div>
  );
}

export default CreateTenantLanding;
